import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { readingModes } from "@/mocks/comics";
import { useSeo } from "@/hooks/useSeo";

const showcaseImages: Record<number, string> = {
  0: "https://readdy.ai/api/search-image?query=Close-up%20of%20hands%20flipping%20comic%20book%20pages%20smoothly%20on%20a%20tablet%2C%20warm%20amber%20lighting%2C%20dynamic%20page%20curl%20animation%2C%20stylized%20manga%20reading%20scene%2C%20cozy%20and%20immersive%2C%20no%20blue%20no%20purple&width=1200&height=800&seq=feat-flip-01&orientation=landscape",
  1: "https://readdy.ai/api/search-image?query=Vertical%20continuous%20comic%20strip%20scrolling%20on%20a%20phone%20screen%2C%20long%20manga%20pages%20flowing%20downward%2C%20warm%20ambient%20light%2C%20stylized%20digital%20reading%20experience%2C%20smooth%20and%20engaging%2C%20no%20blue%20no%20purple&width=1200&height=800&seq=feat-scroll-01&orientation=landscape",
  2: "https://readdy.ai/api/search-image?query=Comic%20chapter%20table%20of%20contents%20menu%20on%20a%20tablet%20with%20tappable%20chapter%20list%2C%20warm%20golden%20lighting%2C%20stylized%20manga%20reader%20interface%2C%20organized%20and%20clean%2C%20cozy%20atmosphere%2C%20no%20blue%20no%20purple&width=1200&height=800&seq=feat-toc-01&orientation=landscape",
  3: "https://readdy.ai/api/search-image?query=Reading%20comics%20at%20night%20in%20bed%20with%20warm%20dim%20glowing%20tablet%20screen%20and%20cozy%20blanket%2C%20dark%20room%20with%20warm%20amber%20screen%20light%2C%20calm%20cozy%20mood%2C%20stylized%20illustration%2C%20eye%20comfort%2C%20no%20blue%20no%20purple&width=1200&height=800&seq=feat-night-01&orientation=landscape",
  4: "https://readdy.ai/api/search-image?query=Downloading%20and%20reading%20comics%20offline%20on%20a%20subway%20train%20phone%2C%20warm%20indoor%20lighting%2C%20stylized%20manga%20reader%20with%20download%20icon%2C%20handy%20and%20portable%2C%20cozy%20atmosphere%2C%20no%20blue%20no%20purple&width=1200&height=800&seq=feat-cache-01&orientation=landscape",
  5: "https://readdy.ai/api/search-image?query=Landscape%20orientation%20reading%20a%20full%20double-page%20comic%20spread%20on%20a%20tablet%2C%20wide%20immersive%20manga%20panels%2C%20warm%20lighting%2C%20stylized%20reader%20experience%2C%20epic%20and%20engaging%2C%20no%20blue%20no%20purple&width=1200&height=800&seq=feat-land-01&orientation=landscape",
};

export default function Features() {
  useSeo(
    "扑飞漫画网阅读功能 - 翻页、下拉、目录跳转、夜间模式、缓存、横屏",
    "扑飞漫画网提供翻页、下拉阅读、目录跳转、夜间模式、离线缓存与横屏六大阅读功能，多种阅读姿势自由切换，追更更畅快。"
  );

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-[72px]">
        <section className="border-b border-background-200/70 bg-background-100/70">
          <div className="mx-auto w-full max-w-7xl px-4 py-14 text-center md:px-8 md:py-20">
            <nav aria-label="面包屑" className="mb-5 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-500">首页</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground-800">阅读功能</span>
            </nav>
            <h1 className="text-3xl leading-snug md:text-5xl">六种阅读姿势，自由切换</h1>
            <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              翻页、下拉、目录跳转、夜间模式、离线缓存、横屏阅读——扑飞漫画网把选择权交给你，打开就能按习惯看。
            </p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-16">
          <div className="space-y-14 md:space-y-20">
            {readingModes.map((m, idx) => (
              <article
                key={m.title}
                className={`grid grid-cols-1 items-center gap-8 lg:grid-cols-2 ${
                  idx % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div>
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-2xl text-primary-600">
                      <i className={m.icon} />
                    </span>
                    <span className="font-heading text-sm text-secondary-400">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h2 className="mt-5 font-heading text-2xl text-foreground-950 md:text-3xl">{m.title}</h2>
                  <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{m.desc}</p>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-500">
                    {idx % 2 === 0
                      ? "点击阅读页顶部即可切换，随时更换你的阅读习惯，无需退出当前章节。"
                      : "设置会自动记忆你的偏好，下次打开继续沿用，无缝衔接阅读状态。"}
                  </p>
                </div>
                <div className="overflow-hidden rounded-xl border border-background-200/70">
                  <img
                    src={showcaseImages[idx]}
                    alt={`扑飞漫画网${m.title}功能展示`}
                    title={`扑飞漫画网${m.title} · 多种阅读姿势自由切换`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 pb-16 md:px-8">
          <div className="flex flex-col items-center gap-4 rounded-xl bg-primary-500 px-6 py-10 text-center">
            <h2 className="text-xl text-background-50 md:text-2xl">准备好切换你的阅读姿势了吗？</h2>
            <p className="max-w-lg text-sm text-background-100">打开扑飞漫画网，选一种最舒服的方式，现在就开始追更。</p>
            <Link
              to="/comics"
              className="mt-2 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
            >
              去漫画库看看 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}